/**
 * Who the documents come from.
 *
 * Templates say what kind of work is being done; issuers say who is generating
 * it. On a page about consumption that is the other half of the question — a
 * supplier who has started sending twice as much is the reason an allowance
 * moved, and no template-level view will show it.
 *
 * The endpoint counts every request on record and takes no date filter, so the
 * card is labelled all-time rather than inheriting the period control.
 */
import { Building2 } from 'lucide-react'
import { Card, EmptyState, HelpTip, ShareBar } from './BillingPrimitives'
import { formatInt } from '../../utils/billing'

export default function IssuersCard({ issuers }) {
  if (!issuers) return null

  const rows = issuers.filter(i => i.request_count > 0)
  const max = Math.max(1, ...rows.map(i => i.request_count))

  return (
    <Card className="p-5 animate-card-in" style={{ animationDelay: '80ms' }}>
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-4 h-4 text-gray-400 dark:text-gray-500" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Top issuers</h2>
        <HelpTip>
          The issuer recorded on each request, usually the supplier the document came from. Useful for
          spotting the counterparty behind a change in volume, which a per-template view cannot show.
        </HelpTip>
        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800
                         text-gray-500 dark:text-gray-400 font-medium">
          All time
        </span>
      </div>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-4">
        Ranked across every request on record. Not filtered by the period above.
      </p>

      {rows.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No issuers recorded"
          body="Requests carry an issuer once extraction identifies one."
        />
      ) : (
        <div className="space-y-1">
          {rows.map((issuer, i) => (
            <div
              key={issuer.name}
              className="flex items-center gap-3 py-1.5 animate-line-in"
              style={{ animationDelay: `${i * 30}ms` }}
            >
              <span
                className="text-[13px] font-medium text-gray-900 dark:text-gray-100 truncate w-[42%]"
                title={issuer.name}
              >
                {issuer.name}
              </span>
              <ShareBar value={issuer.request_count} max={max} label={`${issuer.percentage}%`} />
              <span className="text-[12px] tabular-nums text-gray-600 dark:text-gray-300 w-16 text-right flex-shrink-0">
                {formatInt(issuer.request_count)} req
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
